import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const SuspensionModal = ({ user, onClose, onSuspend }) => {
  const [reason, setReason] = useState('');
  const [reasonType, setReasonType] = useState('violation');
  const [customReason, setCustomReason] = useState('');

  const predefinedReasons = {
    violation: [
      'Violation of Terms of Service',
      'Inappropriate behavior in community',
      'Harassment of other users',
      'Spam or promotional content',
      'Cheating or exploiting games'
    ],
    security: [
      'Suspicious account activity',
      'Potential security breach',
      'Account compromise suspected',
      'Multiple failed login attempts'
    ],
    payment: [
      'Payment fraud detected',
      'Chargeback issues',
      'Invalid payment method',
      'Subscription abuse'
    ],
    other: [
      'Administrative review required',
      'Legal compliance requirement',
      'User requested suspension',
      'Technical investigation needed'
    ]
  };

  const handleReasonChange = (selectedReason) => {
    setReason(selectedReason);
    if (selectedReason === 'custom') {
      setCustomReason('');
    }
  };

  const handleSuspend = () => {
    const finalReason = reason === 'custom' ? customReason : reason;
    
    if (!finalReason.trim()) {
      alert('Please provide a reason for suspension');
      return;
    }

    onSuspend(finalReason.trim());
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg p-6 w-full max-w-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} className="text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                Suspend User Account
              </h3>
              <p className="text-sm text-muted-foreground">
                {user?.full_name} ({user?.email})
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" iconName="X" onClick={onClose} />
        </div>

        <div className="space-y-4">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={16} className="text-yellow-600 dark:text-yellow-400 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                  Account Suspension
                </p>
                <p className="text-yellow-700 dark:text-yellow-300">
                  This user will lose access to their account and receive an email notification 
                  with the suspension reason. They can appeal the decision through support.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Reason Category
            </label>
            <Select
              value={reasonType}
              onChange={(e) => {
                setReasonType(e.target.value);
                setReason('');
                setCustomReason('');
              }}
              options={[
                { value: 'violation', label: 'Terms of Service Violation' },
                { value: 'security', label: 'Security Concerns' },
                { value: 'payment', label: 'Payment Issues' },
                { value: 'other', label: 'Other' }
              ]}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Suspension Reason
            </label>
            <Select
              value={reason}
              onChange={(e) => handleReasonChange(e.target.value)}
              options={[
                { value: '', label: 'Select a reason...' },
                ...predefinedReasons[reasonType].map(r => ({ value: r, label: r })),
                { value: 'custom', label: 'Custom reason...' }
              ]}
            />
          </div>

          {reason === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Custom Reason
              </label>
              <textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Enter detailed reason for suspension..."
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                rows={3}
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {customReason.length}/500 characters
              </p>
            </div>
          )}

          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertCircle" size={16} className="text-red-600 dark:text-red-400 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-red-800 dark:text-red-200 mb-1">
                  Email Notification
                </p>
                <p className="text-red-700 dark:text-red-300">
                  The user will automatically receive an email explaining the suspension 
                  and providing information about the appeal process.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleSuspend}
            disabled={!reason || (reason === 'custom' && !customReason.trim())}
          >
            Suspend Account
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SuspensionModal;
